import React, { useState, useEffect, useRef } from 'react';
import { Radiation } from 'lucide-react';

export default function EnrichmentMeterAnimation({ 
  countryName, 
  fromPercent, 
  toPercent, 
  onComplete 
}) {
  const [currentPercent, setCurrentPercent] = useState(fromPercent);
  const audioRef = useRef(null);
  const mechanicalSoundRef = useRef(null);

  useEffect(() => {
    // Play Geiger counter ticking sound
    const geigerSound = new Audio('/assets/audio/nuke-silo-alert.mp3');
    geigerSound.volume = 0.5;
    geigerSound.loop = true;
    audioRef.current = geigerSound;
    geigerSound.play().catch(e => console.log('Geiger sound play failed:', e));

    // Play mechanical meter sound
    const mechanicalSound = new Audio('/assets/audio/nuke-silo-mechanical.mp3');
    mechanicalSound.volume = 0.6;
    mechanicalSoundRef.current = mechanicalSound;
    mechanicalSound.play().catch(e => console.log('Mechanical sound play failed:', e));

    // Animate percentage from fromPercent to toPercent
    const increment = toPercent > fromPercent ? 1 : -1;
    const duration = 2000; // 2 seconds total
    const steps = Math.abs(toPercent - fromPercent);
    const stepDuration = duration / steps;

    let current = fromPercent;
    const interval = setInterval(() => {
      current += increment;
      setCurrentPercent(current);

      if (
        (increment > 0 && current >= toPercent) ||
        (increment < 0 && current <= toPercent)
      ) {
        clearInterval(interval);
        
        // Stop sounds
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        if (mechanicalSoundRef.current) {
          mechanicalSoundRef.current.pause();
          mechanicalSoundRef.current.currentTime = 0;
        }

        // Wait a moment before completing
        setTimeout(() => {
          onComplete();
        }, 500);
      }
    }, stepDuration);

    return () => {
      clearInterval(interval);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      if (mechanicalSoundRef.current) {
        mechanicalSoundRef.current.pause();
        mechanicalSoundRef.current.currentTime = 0;
      }
    };
  }, [fromPercent, toPercent, onComplete]);

  // Determine color based on percentage
  const getColor = (percent) => {
    if (percent < 33) return '#22c55e'; // Green
    if (percent < 66) return '#f59e0b'; // Orange
    if (percent < 100) return '#ef4444'; // Red
    return '#dc2626'; // Dark red (ready!)
  };

  const isComplete = currentPercent >= 100;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm pointer-events-none">
      <div className="bunker-panel p-8 max-w-md w-full pointer-events-auto animate-pulse-slow">
        {/* Title */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Radiation 
              className="w-10 h-10 animate-spin" 
              style={{ color: getColor(currentPercent) }}
            />
            <h2 
              className="font-heading text-3xl tracking-wider"
              style={{ color: getColor(currentPercent) }}
            >
              URANIUM ENRICHMENT
            </h2>
            <Radiation 
              className="w-10 h-10 animate-spin" 
              style={{ color: getColor(currentPercent) }}
            />
          </div>
          <p className="text-muted text-sm uppercase tracking-widest">{countryName}</p>
        </div>

        {/* Large percentage display */}
        <div className="text-center mb-6">
          <div 
            className="font-mono text-8xl font-bold tabular-nums"
            style={{ 
              color: getColor(currentPercent),
              textShadow: `0 0 20px ${getColor(currentPercent)}40`
            }}
          >
            {currentPercent}%
          </div>
          {isComplete && (
            <div className="text-red-500 text-xl font-bold mt-2 animate-pulse uppercase tracking-wider">
              ☢️ WEAPONS GRADE ACHIEVED ☢️
            </div>
          )}
        </div>

        {/* Progress bar */}
        <div className="relative h-8 bg-gray-800 rounded-lg overflow-hidden border-2 border-gray-700">
          <div
            className="absolute inset-0 transition-all duration-100 ease-linear"
            style={{
              width: `${currentPercent}%`,
              background: `linear-gradient(90deg, ${getColor(currentPercent)}, ${getColor(currentPercent)}dd)`,
              boxShadow: `0 0 20px ${getColor(currentPercent)}60`
            }}
          />
          {/* Animated stripes */}
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)',
              animation: 'slide 1s linear infinite'
            }}
          />
        </div>

        {/* Warning message */}
        <div className="text-center mt-4 text-amber-500 text-sm uppercase tracking-widest animate-pulse">
          {currentPercent < 100 ? (
            '⚠️ ENRICHMENT IN PROGRESS ⚠️'
          ) : (
            '☢️ NUCLEAR CAPABILITY READY ☢️'
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slide {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(20px);
          }
        }
        
        @keyframes pulse-slow {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.02);
          }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
